import {
  HStack,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { capitalize, isEmpty } from "lodash";
import { MdDelete, MdRule } from "react-icons/md";
import { ApprovalInput, Request, WithId } from "types";
import ApprovalStatus from "./ApprovalStatus";
import ApprovalsList from "./ApprovalsList";
import { createItem, deleteItem, hasUserApproved } from "helpers/utils";
import { useEntities } from "./hooks/useEntities";
import { useState } from "react";
import useUser from "./hooks/useUser";
import CommentModal from "./CommentModal";
import { useConfirmation } from "./hooks/useConfirmation";
import { forwardRef, useImperativeHandle } from "react";
import Pagination from "./Pagination";

const SORT = ["-request_date"];
const REQUIRED_FIELDS = [
  "id",
  "requested_by.*",
  "item_id.*",
  "quantity",
  "transaction_type",
  "approval_status",
  "request_date",
  "approvals.*",
  "approvals.approver_role.name",
  "approvals.user_created.id",
  "approvals.user_created.first_name",
  "approvals.user_created.email",
];

const PAGE_SIZE = 25;

interface RequestTableProps {
  filter?: Record<string, any>;
  showDelete?: boolean;
  showReview?: boolean;
  emptyMsg?: string;
}

export interface RequestTableRef {
  findAndUpdate: (id: string, data: WithId<Request>) => void;
}

const RequestTable = forwardRef<RequestTableRef, RequestTableProps>(
  (
    { filter, showDelete, showReview, emptyMsg = "There are no requests" },
    ref
  ) => {
    const toast = useToast();
    const { node: Confirmation, open } = useConfirmation();
    const tableHeaderBackground = useColorModeValue("gray.200", "gray.900");
    const sectionBg = useColorModeValue("blackAlpha.200", "whiteAlpha.200");

    const {
      list,
      isLoading,
      findAndDelete,
      findAndUpdate,
      refetch,
      page,
      pages,
      setPage,
    } = useEntities<WithId<Request>>({
      entity: "Request",
      sort: SORT,
      fields: REQUIRED_FIELDS,
      filter,
      pageSize: PAGE_SIZE,
    });

    useImperativeHandle(ref, () => ({
      findAndUpdate,
    }));

    const { id: userId } = useUser();
    const [selectedRequest, setSelectedRequest] = useState<string>();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const handleDelete = async (id: string) => {
      try {
        await deleteItem<WithId<Request>>("Request", id);
        findAndDelete(id);
        toast({
          title: "Success",
          description: "Request deleted successfully",
          status: "success",
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message ?? "Failed to delete request",
          status: "error",
        });
      }
    };

    const handleApprove = async (comment?: string, satisfactory?: boolean) => {
      if (!selectedRequest) return;
      const request = list.find((r) => r.id === selectedRequest);

      if (!request) {
        toast({
          title: "Error",
          description: "Request not found",
          status: "error",
        });
        return;
      }

      if (hasUserApproved(request.approvals, userId)) {
        toast({
          title: "Error",
          description: "You have already reviewed this request",
          status: "error",
        });
        setSelectedRequest(undefined);
        return;
      }

      try {
        await createItem<Partial<ApprovalInput>>("Approval", {
          request_id: selectedRequest,
          comment,
          satisfactory,
        });

        toast({
          title: "Success",
          description: `Request ${
            satisfactory ? "approved" : "disapproved"
          } successfully`,
          status: "success",
        });

        refetch();
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message ?? "Failed to process request",
          status: "error",
        });
      }
      setSelectedRequest(undefined);
    };

    return (
      <>
        <VStack
          background={isEmpty(list) ? sectionBg : undefined}
          alignItems={list.length === 0 && !isLoading ? "center" : undefined}
          justifyContent={
            list.length === 0 && !isLoading ? "center" : undefined
          }
          height={"100%"}
          p={2}
        >
          {list.length === 0 && !isLoading && (
            <Text fontSize="sm">{emptyMsg}</Text>
          )}
          {isLoading && <Spinner />}

          {!isEmpty(list) && !isLoading && (
            <TableContainer overflowY={"scroll"} className="tiny-scrollbar">
              <Table colorScheme="teal">
                <Thead
                  position={"sticky"}
                  top={"0px"}
                  zIndex={99}
                  bg={tableHeaderBackground}
                >
                  <Tr>
                    <Th>#</Th>
                    <Th>Requested By</Th>
                    <Th>Item Name</Th>
                    <Th>Transaction Type</Th>
                    <Th>Approval Status</Th>
                    <Th isNumeric>Quantity</Th>
                    <Th isNumeric>Approvals Given</Th>
                    <Th>Request Date</Th>
                    {(showDelete || showReview) && <Th></Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {list.map((request, index) => (
                    <Tr key={request.id}>
                      <Td>{PAGE_SIZE * (page - 1) + index + 1}</Td>
                      <Td>{`${request.requested_by?.first_name} ${request.requested_by?.last_name}`}</Td>
                      <Td>{request.item_id.name}</Td>
                      <Td>{capitalize(request.transaction_type)}</Td>
                      <Td>
                        <ApprovalStatus status={request.approval_status} />
                      </Td>
                      <Td isNumeric>{request.quantity}</Td>
                      <Td isNumeric>
                        <ApprovalsList approvals={request.approvals}>
                          <Text cursor="pointer" textDecoration="underline">
                            {request.approvals.length}
                          </Text>
                        </ApprovalsList>
                      </Td>
                      <Td>
                        {format(new Date(request.request_date), "yyyy-MM-dd")}
                      </Td>
                      {(showDelete || showReview) && (
                        <Td>
                          <HStack>
                            {request.approval_status === "pending" &&
                              !hasUserApproved(request.approvals, userId) && (
                                <>
                                  {showReview &&
                                    request.requested_by.id !== userId && (
                                      <IconButton
                                        aria-label="Review"
                                        icon={<MdRule />}
                                        colorScheme="blue"
                                        onClick={() => {
                                          setSelectedRequest(request.id);
                                          onOpen();
                                        }}
                                      />
                                    )}
                                  {showDelete && (
                                    <IconButton
                                      aria-label="Delete"
                                      icon={<MdDelete />}
                                      colorScheme="red"
                                      onClick={() =>
                                        open({
                                          title: "Delete Request",
                                          description:
                                            "Are you sure you want to delete this request?",
                                          onConfirm: () =>
                                            handleDelete(request.id),
                                          actions: ["Cancel", "Delete"],
                                        })
                                      }
                                    />
                                  )}
                                </>
                              )}
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
          {!isEmpty(list) && !isLoading && pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              onPageChange={setPage}
            />
          )}
        </VStack>
        <CommentModal
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleApprove}
          title="Approve or Disapprove Request"
        />
        {Confirmation}
      </>
    );
  }
);

export default RequestTable;
